import { IconButton } from '@components/core/icon-button.component';
import { MainLayout } from '@components/layouts/main.layout';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { Tile } from '@components/shared/tile.component';
import { Button } from '@components/ui/button';
import { useRequireAdmin } from '@hooks/use-require-admin.hook';
import { useAdminUsesQuery } from '@queries/admin-uses.query';
import { PlusIcon, RefreshCcwIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { AdminUseCreateModal } from './components/admin-use-create-modal.component';
import { AdminUseModal } from './components/admin-use-modal.component';
import { AdminUsesPagination } from './components/admin-uses-pagination.component';
import { AdminUsesTable } from './components/admin-uses-table.component';
import { useAdminUsesQueryParams } from './hooks/use-admin-uses-query-params.hook';

export const AdminUsesPage: React.FC = () => {
  const { isAdmin, isPending: isAdminPending } = useRequireAdmin();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const {
    DEFAULT_SORTING,
    handleLimitChange,
    page,
    queryParams,
    search,
    selectedUseId,
    setPage,
    setSearch,
    setSelectedUseId,
    setSorting,
    sortingState,
  } = useAdminUsesQueryParams();

  const { data, isPending, isFetching, refetch } = useAdminUsesQuery(queryParams);

  const uses = data?.uses ?? [];
  const pagination = data?.pagination;

  useEffect(() => {
    if (!pagination) return;

    const current = page;
    const total = pagination.totalPages ?? 0;

    if (total < 1) {
      if (current !== 1) void setPage(1);
      return;
    }

    if (current > total) {
      void setPage(Math.max(1, total));
    }
  }, [page, pagination, setPage]);

  if (isAdminPending || !isAdmin) {
    return null;
  }

  return (
    <PageviewTracker>
      <HelmetWrapper title="Admin Uses" />
      <MainLayout
        asideClassName="hidden"
        contentClassName="w-full md:max-w-full"
      >
        <div className="flex flex-col w-full gap-6">
          <Tile
            header={{
              title: 'Uses',
              actions: (
                <div className="flex items-center gap-2">
                  <IconButton
                    ariaLabel="Refresh uses"
                    disabled={isFetching}
                    icon={<RefreshCcwIcon />}
                    variant="secondary"
                    onClick={() => {
                      refetch();
                    }}
                  />
                  <Button onClick={() => setIsCreateOpen(true)}>
                    <PlusIcon className="size-3 mr-1" />
                    New Use
                  </Button>
                </div>
              ),
            }}
          >
            <div className="flex flex-col gap-4">
              <AdminUsesTable
                isLoading={isPending}
                onRowClick={(useId) => void setSelectedUseId(useId)}
                onSearchChange={(value) => {
                  void setSearch(value);
                  void setPage(1);
                }}
                onSortingChange={(state) => {
                  if (!state || state.length === 0) {
                    void setSorting(DEFAULT_SORTING.map((item) => ({ ...item })));
                    return;
                  }
                  void setSorting(state);
                }}
                search={search}
                sorting={sortingState}
                uses={uses}
              />

              {pagination && (
                <AdminUsesPagination
                  isFetching={isFetching}
                  pagination={pagination}
                  onLimitChange={handleLimitChange}
                  onNextPage={() => void setPage(page + 1)}
                  onPreviousPage={() => void setPage(page - 1)}
                />
              )}
            </div>
          </Tile>
        </div>
      </MainLayout>

      <AdminUseCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />

      {selectedUseId && (
        <AdminUseModal
          isOpen
          useId={selectedUseId}
          onClose={() => void setSelectedUseId(null)}
        />
      )}
    </PageviewTracker>
  );
};
